<template>
  <div
    v-if="getInitialized"
    :class="size === 'small' ? 'overflow-x-hidden': ''"
  >
    <div
      v-if="showPromptAI && !readonly"
      class="flex justify-content-left align-items-left mb-3"
    >
      <SegmentationPromptAI
        @generate-config-in-progress="AIGenerationInProgress = true"
        @generate-config="handleGenerationByAI"
      />
    </div>
    <div class="flex flex-column flex-grow-1 rule-list relative">
      <Loader
        v-if="AIGenerationInProgress"
      />
      <div
        v-for="(blockAND, index) in readonlyConfig"
        :key="`blockAND-${index}-${cleanedDataKey}`"
      >
        <div
          v-if="Number(index) !== 0"
          class="connector"
        >
          <div
            v-if="!readonly"
            class="connector-choose-button"
          >
            <div
              class="connector-option and"
            >
              {{ t('andUpper') }}
            </div>
            <div
              class="connector-option or"
              @click="moveBlockAndToBlockOr(index)"
            >
              {{ t('orUpper') }}
            </div>
          </div>
          <div
            v-else
            class="connector-readonly"
          >
            {{ t('andUpper') }}
          </div>
        </div>
        <div
          class="rule-container relative"
        >
          <SegmentationBlockAND
            :key="`blockAND-${index}-${refreshComponents}`"
            :index-block-and="Number(index)"
            :block-and="blockAND"
            :size="size"
            :theme="theme"
            :readonly="readonly"
          />
        </div>
      </div>
    </div>
  </div>
  <Loader v-else />
</template>

<script lang="ts">
import {
  computed,
  defineComponent, watch, ref, PropType, SetupContext, onBeforeUnmount, onMounted,
} from 'vue';
import SegmentationBlockAND from '@/components/segmentations/SegmentationBlockAND.vue';
import { useI18n } from 'vue-i18n';
import { store } from '@/store';
import Loader from '@/components/layout/Loader.vue';
import axios from 'axios';
import { UserState } from '@/composables/User';
import { showToastError, showToastSuccess } from '@/helpers';
import SegmentationPromptAI from '@/components/segmentations/SegmentationPromptAI.vue';
import { TRIGGER_SEGMENTATION_ORIGIN } from '@/composables/segmentations/triggerSegmentation';

export default defineComponent({
  name: 'SegmentationsPanel',
  components: {
    SegmentationPromptAI,
    Loader,
    SegmentationBlockAND,
  },

  props: {
    modelValue: {
      type: Object as PropType<any>,
      required: false,
      default: () => ({}),
    },

    origin: {
      type: String,
      required: false,
      default: '',
    },

    size: {
      type: String,
      required: false,
      default: '',
    },

    showPromptAI: {
      type: Boolean,
      required: false,
      default: false,
    },

    theme: {
      type: [String, Array] as PropType<string | string[]>,
      required: false,
      default: '',
    },

    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },

    triggerCount: {
      type: Number,
      required: false,
      default: 0,
    },

    // Flow context for new flow system (events metadata support)
    flowData: {
      type: Object as PropType<Record<string, any> | null>,
      required: false,
      default: null,
    },

    operatorStepId: {
      type: String,
      required: false,
      default: '',
    },

    // Collection contexts for parent loops (custom collections, products, etc.)
    collectionContexts: {
      type: Array as PropType<{ collection: string; iterator: string; schema?: any }[]>,
      required: false,
      default: () => [],
    },
  },

  emits: ['update:modelValue', 'count-updated'],

  setup(props: {
    modelValue: any;
    origin: string;
    size: string;
    showPromptAI: boolean;
    theme: string | string[];
    readonly: boolean;
    triggerCount: number;
    flowData: any;
    operatorStepId: string;
    collectionContexts: { collection: string; iterator: string; schema?: any }[];
  }, { emit }: SetupContext) {
    const { t } = useI18n();
    const idShop = UserState?.activeShop?.id ?? 0;
    const cleanedDataKey = ref(0);
    const AIGenerationInProgress = ref(false);
    const segmentsConfiguration: any = computed(() => store.getters['segmentationsEditor/getConfiguration']);

    // In readonly mode, use modelValue directly; otherwise use store
    const readonlyConfig = computed(() => {
      if (props.readonly) {
        return props.modelValue || [];
      }
      return segmentsConfiguration.value;
    });

    const refreshComponents: any = computed(() => store.getters['segmentationsEditor/getRefreshComponents']);

    const getInitialized: any = computed(() => store.getters['segmentationsEditor/getInitialized']);

    const refreshContactsNumber = computed(() => store.getters['segmentationsEditor/getRefreshContactsNumber']);

    const loadContactsNumber = ref(false);

    const totalContactsNumber: any = ref(0);

    const cleanData = (data: any) => {
      const cleanedData = data.map((subArray: any) => subArray.filter((obj: any) => Object.keys(obj).length !== 0));
      return cleanedData.filter((subArray: any) => subArray.length > 0);
    };

    const handleContactsNumber = async () => {
      // A trigger's segmentation is evaluated per visitor when the page loads:
      // a contact count means nothing there, and its visit criteria are unknown
      // to the counting query.
      if (props.origin === TRIGGER_SEGMENTATION_ORIGIN) {
        store.commit('segmentationsEditor/setRefreshContactsNumber', false);
        return;
      }
      emit('count-updated', { count: null, loading: true });
      loadContactsNumber.value = true;
      const tmpCleanedData = cleanData(segmentsConfiguration.value);
      try {
        const result = await axios.post(
          `${process.env.VUE_APP_NESTJS_SEGMENTATION_API_URL}/count`,
          {
            idShop,
            config: tmpCleanedData,
          },
        );

        if (result && (result.status === 200 || result.status === 201)) {
          if (result.data.error) {
            totalContactsNumber.value = null;
          } else {
            totalContactsNumber.value = result.data;
          }
        } else {
          totalContactsNumber.value = 0;
          await showToastError('GENERIC_ERROR');
        }
      } catch (e) {
        totalContactsNumber.value = 0;
        await showToastError('GENERIC_ERROR');
      }

      loadContactsNumber.value = false;
      store.commit('segmentationsEditor/setRefreshContactsNumber', false);

      // Emit count result to parent
      emit('count-updated', {
        count: totalContactsNumber.value,
        loading: false,
      });
    };

    const handleGenerationByAI = async (generatedConfig: any) => {
      if (generatedConfig.success === false) {
        AIGenerationInProgress.value = false;
        await showToastError(t('segmentations.promptAI.error'));
        return;
      }

      store.commit('segmentationsEditor/setConfiguration', generatedConfig.config);
      await handleContactsNumber();
      AIGenerationInProgress.value = false;
      await showToastSuccess(t('segmentations.promptAI.success'));
    };

    const moveBlockAndToBlockOr = (index: any) => {
      store.commit('segmentationsEditor/moveBlockAndToBlockOr', index);
      handleContactsNumber();
    };

    watch([segmentsConfiguration], () => {
      if (!segmentsConfiguration.value.length || !segmentsConfiguration.value) {
        store.commit('segmentationsEditor/initConfiguration');
      }
      emit('update:modelValue', segmentsConfiguration.value);
    }, { deep: true });

    watch([refreshContactsNumber], async () => {
      // TODO : only if comming from import page
      if (refreshContactsNumber.value) {
        await handleContactsNumber();
      }
    });

    // Watch for triggerCount changes from parent to refresh count
    // Wait for store to be initialized before processing
    watch(() => props.triggerCount, async (newVal, oldVal) => {
      if (newVal !== oldVal && newVal > 0 && getInitialized.value) {
        await handleContactsNumber();
      }
    });

    // Watch for theme changes and reinitialize
    watch(() => props.theme, async (newTheme, oldTheme) => {
      if (newTheme !== oldTheme && getInitialized.value) {
        await store.dispatch('segmentationsEditor/initialization',
          {
            originCall: props.origin,
            configSegmentation: props.modelValue,
            size: props.size,
            theme: newTheme,
            flow: props.flowData,
            operatorStepId: props.operatorStepId,
            collectionContexts: props.collectionContexts,
          });
      }
    });

    // Watch for collectionContexts changes and reinitialize (async detection)
    watch(() => props.collectionContexts, async (newContexts, oldContexts) => {
      // Reinitialize when contexts are detected (handles async detection race condition)
      if (newContexts && newContexts.length > 0) {
        // Only reinitialize if contexts actually changed
        const hasChanged = !oldContexts
          || oldContexts.length === 0
          || newContexts.length !== oldContexts.length
          || newContexts.some((ctx, i) => ctx.collection !== oldContexts[i]?.collection);

        if (hasChanged) {
          await store.dispatch('segmentationsEditor/initialization',
            {
              originCall: props.origin,
              configSegmentation: props.modelValue,
              size: props.size,
              theme: props.theme,
              flow: props.flowData,
              operatorStepId: props.operatorStepId,
              collectionContexts: newContexts,
            });
        }
      }
    }, { deep: true });

    onMounted(async () => {
      await store.dispatch('segmentationsEditor/initialization',
        {
          originCall: props.origin || 'conditional',
          configSegmentation: props.modelValue,
          size: props.size,
          theme: props.theme,
          flow: props.flowData,
          operatorStepId: props.operatorStepId,
          collectionContexts: props.collectionContexts,
        });
    });

    onBeforeUnmount(() => {
      // Don't reset state in readonly mode to avoid side effects
      if (!props.readonly) {
        store.commit('segmentationsEditor/resetState');
      }
    });

    return {
      t,
      cleanedDataKey,
      getInitialized,
      refreshComponents,
      segmentsConfiguration,
      readonlyConfig,
      handleGenerationByAI,
      AIGenerationInProgress,
      moveBlockAndToBlockOr,
    };
  },
});
</script>

<style lang="scss" scoped>
.contact-number-container {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  border: 1px solid #dee2e6;
}

.connector {
  position: relative;
  width: 100%;
  height: 4rem;
}

.line {
  width: 1px;
  height: 100%;
  background-color: #e9ecef;
  position: relative;
  left: 2rem; /* Position à 10px du bord gauche */
}

.connector-choose-button {
  position: absolute;
  z-index: 2;
  left: 0;
  top: 50%;
  transform: translate(0, -50%);
  text-align: center;
  background-color: white;
  border-radius: 5px;
  font-weight: bold;
  display: flex;
  width: 10rem;
  height: 40px;
  border: 1px solid #ccc;
  overflow: hidden;
  cursor: pointer;
}

.connector-option {
  flex: 1;
  display: flex;
  width: 50%;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  background: white;
  transition: background 0.3s, color 0.3s;

  &.and {
    color: white;
    cursor: pointer;
    background: #495057;
  }

  &.or {
    cursor: pointer;
    color: black;

    &:hover {
      background: #f8f9fa;
    }
  }
}

.connector-readonly {
  position: absolute;
  z-index: 2;
  left: 0;
  top: 50%;
  transform: translate(0, -50%);
  text-align: center;
  background-color: #495057;
  color: white;
  border-radius: 5px;
  font-weight: bold;
  font-size: 12px;
  padding: 0.25rem 0.75rem;
}
</style>

<style lang="scss">
.and-tag .p-tag {
  background-color: #e9ecef;
  color: black;
}
</style>
